import { ZipcodeLocationResponse, useFetchZipcodeLocationQuery } from '@ee-api/zipcode-api/zipcodeService';
import { useAppSelector } from '@redux/hooks';
import { selectZipcode } from '@redux/slices/zipcodeSlice';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';

import { AxiosBaseQueryFunction } from '../../axiosBaseQuery';

type useZipcodeLocationReturn = TypedUseQueryHookResult<ZipcodeLocationResponse, string, AxiosBaseQueryFunction> & {
    zipcodeLocation?: ZipcodeLocationResponse;
    isFetchingZipcodeLocation: boolean;
    isErrorZipcodeLocation: boolean;
};

/**
 * Hook for looking up the city/state for a given zipcode in the Redux store.
 *
 * @remarks
 * - The zip code should always be exactly 5 numeric digits, otherwise it's a wasted call.
 * - Cached results are used whenever possible, as zip codes are relatively static.
 * - The function returns location information for the zip code or a 404 error if the zip code is not found.
 */
const useZipcodeLocation = (): useZipcodeLocationReturn => {
    const zipcode = useAppSelector(selectZipcode);

    const { zipcodeLocation, ...rest } = useFetchZipcodeLocationQuery(zipcode ? zipcode : skipToken, {
        selectFromResult: (result) => ({ zipcodeLocation: result.isError ? undefined : result.data, ...result }),
    });

    return {
        zipcodeLocation,
        isFetchingZipcodeLocation: rest.isFetching,
        isErrorZipcodeLocation: rest.isError,
        ...rest,
    };
};

export default useZipcodeLocation;
